import React, { useEffect, useRef } from 'react';
import { Target, Users, Landmark, Clock, Award } from 'lucide-react';
import { motion } from 'framer-motion';
import Typed from 'typed.js';
import SEO from '../components/SEO';

const About = () => {
    const typedElement = useRef(null);

    useEffect(() => {
        const typed = new Typed(typedElement.current, {
            strings: [
                'Innovation',
                'Excellence',
                'Transformation',
                'Quality',
                'Partnership'
            ],
            typeSpeed: 80,
            backSpeed: 50,
            backDelay: 2000,
            loop: true,
            showCursor: true,
            cursorChar: '|',
        });

        return () => typed.destroy();
    }, []);

    return (
        <div className="bg-white dark:bg-dark transition-colors duration-500 min-h-screen">
            <SEO
                title="About Us | Leading IT Solution Provider - MehareTech"
                description="Discover the story of MehareTech. We are a premier IT Services & Software Development company dedicated to delivering high-impact technological innovation and excellence since 2012."
                keywords="about MehareTech, Indian IT company, software development history, tech innovation leaders, offshore development center, digital transformation agency"
            />
            {/* Hero Section */}
            <section className="relative pt-24 md:pt-32 pb-12 md:pb-20 bg-slate-900 dark:bg-[#02000c] overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div className="absolute top-0 right-0 w-1/2 h-full bg-primary/20 blur-[120px]"></div>
                    <div className="absolute bottom-0 left-0 w-1/4 h-full bg-primary/5 blur-[100px]"></div>
                </div>
                <div className="container mx-auto px-6 relative z-10 text-center">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-primary font-bold uppercase tracking-[0.2em] text-sm mb-4 block"
                    >
                        Our DNA
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-3xl md:text-5xl font-black text-white mb-4 md:mb-6"
                    >
                        Driven by <span className="text-gradient" ref={typedElement}></span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto"
                    >
                        Architecting the digital future with precision, passion, and purpose.
                    </motion.p>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-12 md:py-24">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-20 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.8 }}
                            className="space-y-8"
                        >
                            <span className="text-primary font-bold uppercase tracking-widest text-sm">The MehareTech Way</span>
                            <h2 className="text-2xl md:text-4xl font-bold text-slate-900 dark:text-white leading-tight">
                                Your Partner in <span className="text-gradient">Digital Evolution</span>
                            </h2>
                            <p className="text-slate-600 dark:text-slate-400 text-base md:text-lg leading-relaxed">
                                MehareTech is a premier IT Services & Software Development company dedicated to delivering
                                high-impact technology solutions. Founded on the principles of innovation and beauty,
                                we help businesses navigate the complex digital landscape with confidence.
                            </p>
                            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                                Our mission is to empower organizations by providing scalable, secure, and user-centric
                                digital products. Whether you are a startup looking to make your mark or an established
                                enterprise aiming for modernization, we have the expertise to bring your vision to life.
                            </p>

                            <div className="grid grid-cols-2 gap-4 md:gap-6 pt-4 md:pt-6">
                                <div className="p-5 md:p-8 bg-slate-50 dark:bg-dark-accent/40 rounded-3xl border border-slate-100 dark:border-white/5 shadow-sm group hover:shadow-xl transition-all">
                                    <h4 className="text-2xl md:text-4xl font-black text-primary mb-1 md:mb-2 group-hover:scale-110 transition-transform origin-left">150+</h4>
                                    <p className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em]">Projects Delivered</p>
                                </div>
                                <div className="p-5 md:p-8 bg-slate-50 dark:bg-dark-accent/40 rounded-3xl border border-slate-100 dark:border-white/5 shadow-sm group hover:shadow-xl transition-all">
                                    <h4 className="text-2xl md:text-4xl font-black text-primary mb-1 md:mb-2 group-hover:scale-110 transition-transform origin-left">99%</h4>
                                    <p className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em]">Client Satisfaction</p>
                                </div>
                            </div>
                        </motion.div>

                        <div className="grid gap-8">
                            {[
                                {
                                    icon: Target,
                                    title: 'Our Vision',
                                    text: 'To be a global leader in providing innovative technology solutions that transform how people live and work.'
                                },
                                {
                                    icon: Users,
                                    title: 'Our Team',
                                    text: 'A passionate group of designers, developers, and strategists committed to technical excellence.'
                                },
                                {
                                    icon: Award,
                                    title: 'Our Values',
                                    text: 'Integrity, innovation, and impact. We don\'t just build software; we build success stories.'
                                }
                            ].map((item, idx) => (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, x: 50 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    transition={{ duration: 0.6, delay: idx * 0.1 }}
                                    whileHover={{ x: 10 }}
                                    className="p-4 md:p-8 bg-white dark:bg-dark-accent/10 rounded-[1.5rem] md:rounded-[2.5rem] border border-slate-100 dark:border-white/5 shadow-sm hover:shadow-2xl hover:shadow-primary/5 transition-all flex gap-4 md:gap-8 items-center"
                                >
                                    <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/10 rounded-xl md:rounded-2xl flex items-center justify-center shrink-0 text-primary group-hover:bg-primary group-hover:text-white transition-all">
                                        <item.icon className="w-6 h-6 md:w-[30px] md:h-[30px]" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold mb-1 md:mb-2 text-slate-900 dark:text-white">{item.title}</h3>
                                        <p className="text-slate-500 dark:text-slate-400 text-xs md:text-sm leading-relaxed">{item.text}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Info Section */}
            <section className="py-12 md:py-20 bg-slate-50 dark:bg-dark-accent/20 border-y border-slate-100 dark:border-white/5">
                <div className="container mx-auto px-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="p-6 md:p-10 bg-white dark:bg-dark-accent rounded-[1.5rem] md:rounded-[2.5rem] shadow-sm border border-slate-100 dark:border-white/5 flex items-center gap-4 md:gap-8"
                        >
                            <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/10 text-primary rounded-xl md:rounded-2xl flex items-center justify-center shrink-0">
                                <Landmark className="w-6 h-6 md:w-[32px] md:h-[32px]" />
                            </div>
                            <div>
                                <h3 className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1 md:mb-2">Business Registry</h3>
                                <p className="text-base md:text-lg font-bold text-slate-900 dark:text-white">IT Services & Software Development</p>
                            </div>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="p-6 md:p-10 bg-white dark:bg-dark-accent rounded-[1.5rem] md:rounded-[2.5rem] shadow-sm border border-slate-100 dark:border-white/5 flex items-center gap-4 md:gap-8"
                        >
                            <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/10 text-primary rounded-xl md:rounded-2xl flex items-center justify-center shrink-0">
                                <Clock className="w-6 h-6 md:w-[32px] md:h-[32px]" />
                            </div>
                            <div>
                                <h3 className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1 md:mb-2">Lighthouse Hours</h3>
                                <p className="text-base md:text-lg font-bold text-slate-900 dark:text-white">Mon – Sat: 10:00 AM – 7:00 PM (IST)</p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default About;
